import { microCopy, type MicroCopyValues } from "@bmi-digital/dxb-microcopies";
import type { GetMicroCopy } from "../../MicroCopy";

const isPositiveNumber = (value: string) => Number.parseFloat(value) > 0;

type GetErrorMessage = (
  errorType: "range" | MicroCopyValues,
  placeholders?: Record<string, string>
) => string;

const numberValidator = (getErrorMessage: GetErrorMessage, value?: string) => {
  if (!value) {
    return getErrorMessage(microCopy.VALIDATION_ERRORS_FIELD_REQUIRED);
  }

  if (!isPositiveNumber(value)) {
    return getErrorMessage(microCopy.VALIDATION_ERRORS_POSITIVE_NUMBERS_ONLY);
  }
};

type RangeValidatorCreator = (params: {
  min: number;
  max: number;
  getMicroCopy: GetMicroCopy;
  rangeErrorKey: MicroCopyValues;
}) => (value?: string) => string | undefined;

const rangeValidator: RangeValidatorCreator =
  ({ min, max, getMicroCopy, rangeErrorKey }) =>
  (value) => {
    const error = numberValidator(getMicroCopy, value);
    if (error) {
      return error;
    }

    //value is always defined at this step,
    // as the numberValidator function returns an error if it is not defined.
    const number = parseFloat(value!);

    if (!number || number < min || number > max) {
      return getMicroCopy(rangeErrorKey, {
        min: min.toString(),
        max: max.toString()
      });
    }
  };

export type Type = "LENGTH" | "PROTRUSION_LENGTH" | "PITCH";

type TypeProps = {
  unit: string;
  validator: (value?: string) => string | undefined;
  helperText?: string;
};

export const getFieldTypes = (
  getMicroCopy: GetMicroCopy
): {
  [key in Type]: TypeProps;
} => ({
  LENGTH: {
    unit: getMicroCopy(microCopy.ROOF_DIMENSIONS_UNITS_METERS_LABEL),
    validator: rangeValidator({
      min: 2,
      max: 200,
      getMicroCopy,
      rangeErrorKey: microCopy.VALIDATION_ERRORS_LENGTH_RANGE
    }),
    helperText: getMicroCopy(microCopy.VALIDATION_ERRORS_LENGTH_RANGE, {
      min: "2",
      max: "200"
    })
  },
  PROTRUSION_LENGTH: {
    unit: getMicroCopy(microCopy.ROOF_DIMENSIONS_UNITS_METERS_LABEL),
    validator: rangeValidator({
      min: 0.5,
      max: 200,
      getMicroCopy,
      rangeErrorKey: microCopy.VALIDATION_ERRORS_LENGTH_RANGE
    }),
    helperText: getMicroCopy(microCopy.VALIDATION_ERRORS_LENGTH_RANGE, {
      min: "0.5",
      max: "200"
    })
  },
  PITCH: {
    unit: getMicroCopy(microCopy.ROOF_DIMENSIONS_UNITS_DEGREES_LABEL),
    validator: rangeValidator({
      min: 6,
      max: 85,
      getMicroCopy,
      rangeErrorKey: microCopy.VALIDATION_PITCH_RANGE
    }),
    helperText: getMicroCopy(microCopy.VALIDATION_PITCH_RANGE, {
      min: "6",
      max: "85"
    })
  }
});
